// ─── Chi è entrato, e cosa può vedere ────────────────────────────────────────
//
// 🚨 IL FRONTEND NASCONDE PER CORTESIA, NON PER SICUREZZA (§ 03.2). Tutto
// quello che sta qui dentro serve a non mostrare a qualcuno un bottone che
// riceverebbe un rifiuto: a decidere davvero è il server, e lo fa a ogni
// richiesta. Chi togliesse queste guardie renderebbe l'applicativo sgraziato,
// non insicuro.

import { useSyncExternalStore } from 'react';
import type { Permesso, Profilo } from '@pmo/shared';
import { osservaSessione, profiloCorrente, riprendi, sessioneRipresa } from '../../lib/api.js';

/**
 * Il tentativo di riprendere la sessione dal cookie si fa UNA volta per
 * caricamento di pagina, e la promessa è di modulo.
 *
 * ⚠️ Non è una micro-ottimizzazione: in sviluppo React monta i componenti due
 * volte (`StrictMode`), e due rinnovi con lo stesso cookie sono — per il
 * server, giustamente — un gettone rigiocato, cioè il sintomo di un furto.
 * L'applicativo si butterebbe fuori da solo a ogni ricaricamento.
 */
let avvio: Promise<void> | null = null;

function avvia(): Promise<void> {
  // A dire «fatto» è `lib/api.ts`, che è anche il posto da cui parte l'avviso
  // agli ascoltatori: tenere qui una seconda bandierina vorrebbe dire alzarla
  // senza che nessuno se ne accorga.
  avvio ??= riprendi().then(() => undefined);
  return avvio;
}

export interface Sessione {
  profilo: Profilo | null;
  /** `false` finché non si sa se c'è una sessione da riprendere. */
  pronta: boolean;
}

export function useSessione(): Sessione {
  const profilo = useSyncExternalStore(osservaSessione, profiloCorrente, () => null);
  const pronta = useSyncExternalStore(osservaSessione, sessioneRipresa, () => false);
  if (!pronta) void avvia();
  return { profilo, pronta };
}

export function puòVedere(profilo: Profilo | null, permesso: Permesso): boolean {
  // ⚖️ Qui non si riscrive la regola di `puo()`: `/me` manda già i permessi
  // EFFETTIVI, con `owner` e `admin` già espansi. Una seconda copia della
  // regola è una copia che un giorno dice una cosa diversa dal server.
  return profilo?.permissions.includes(permesso) ?? false;
}

export function SeHaPermesso({
  nome,
  children,
}: {
  nome: Permesso;
  children: React.ReactNode;
}): React.JSX.Element | null {
  const { profilo } = useSessione();
  return puòVedere(profilo, nome) ? <>{children}</> : null;
}

/** La pagina intera, quando il permesso manca: si dice, non si mostra il vuoto. */
export function RichiediPermesso({
  nome,
  children,
}: {
  nome: Permesso;
  children: React.ReactNode;
}): React.JSX.Element {
  const { profilo } = useSessione();
  if (puòVedere(profilo, nome)) return <>{children}</>;
  return (
    <section className="riquadro vuoto">
      <h2>Questa sezione non è aperta per questo accesso</h2>
      <p className="tenue">
        Se dovrebbe esserlo, la spunta la mette un proprietario da Impostazioni → Utenti staff.
      </p>
    </section>
  );
}
